import html2canvas from 'html2canvas';
import {
  Card,
  CardContent,
  Divider,
  Grid,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Paper,
} from "@mui/material";
import { Box } from "@mui/system";
import React, { useEffect, useRef, useState } from "react";
import bodyDiagram from "../../../../assets/generated-image (42) 2.png"; // replace with your body diagram

// const sections = [
//   "heent",
//   "neck",
//   "chest",
//   "abdomen",
//   "pelvis",
//   "back",
//   "upper_limb",
//   "lower_limb",
//   "general",
// ];

const sections = [
  { id: "heent", name: "Heent" },
  { id: "neck", name: "Neck" },
  { id: "chest", name: "Chest" },
  { id: "abdomen", name: "Abdomen" },
  { id: "pelvis", name: "Pelvis" },
  { id: "back", name: "Back" },
  { id: "upper_limb", name: "Upper Limb" },
  { id: "lower_limb", name: "Lower Limb" },
  { id: "general", name: "General" },
];

const baselineMap = { A: 1, V: 2, P: 3, U: 4 };
const airwayMap = { Open: 1, Closed: 2 };
const breathingMap = { Present: 1, Absent: 2 };
const circulationMap = { Radial: 1, Present: 2, Absent: 3 };
const skinPerfusionMap = { Normal: 1, Abnormal: 2 };

function StepThreeAssesment({
  formData,
  handleChange,
  existingMarks = [],
  setFormData,
  errors,
}) {
  const [activeSection, setActiveSection] = useState(null);
  const [marks, setMarks] = useState(existingMarks);
  const imgRef = useRef();
  const [currentInput, setCurrentInput] = useState(null);
  const canvasRef = useRef();
  const [isDrawing, setIsDrawing] = useState(false);
  const [lines, setLines] = useState([]);
  const [capturedImage, setCapturedImage] = useState(null);
  const [canvasOffset, setCanvasOffset] = useState({ left: 0, top: 0, width: 0, height: 0 });

  useEffect(() => {
    setMarks(existingMarks);
  }, [existingMarks]);

  useEffect(() => {
    const resizeCanvas = () => {
      if (!canvasRef.current || !imgRef.current) return;

      const img = imgRef.current;
      const canvas = canvasRef.current;
      const box = img.parentElement;

      const containerWidth = box.clientWidth;
      const containerHeight = box.clientHeight;
      const naturalWidth = img.naturalWidth;
      const naturalHeight = img.naturalHeight;

      const scale = Math.min(containerWidth / naturalWidth, containerHeight / naturalHeight);
      const displayedWidth = naturalWidth * scale;
      const displayedHeight = naturalHeight * scale;
      const offsetX = (containerWidth - displayedWidth) / 2;
      const offsetY = (containerHeight - displayedHeight) / 2;

      canvas.width = displayedWidth;
      canvas.height = displayedHeight;
      canvas.style.left = offsetX + 'px';
      canvas.style.top = offsetY + 'px';

      setCanvasOffset({ left: offsetX, top: offsetY, width: displayedWidth, height: displayedHeight });

      redrawCanvas();
    };

    window.addEventListener("resize", resizeCanvas);
    resizeCanvas(); // initial

    return () => window.removeEventListener("resize", resizeCanvas);
  }, [imgRef.current]);

  const redrawCanvas = () => {
    const ctx = canvasRef.current.getContext("2d");
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);

    ctx.strokeStyle = "red";
    ctx.lineWidth = 2;

    lines.forEach((line) => {
      ctx.beginPath();
      ctx.moveTo(line.points[0].x, line.points[0].y);
      line.points.forEach((p) => ctx.lineTo(p.x, p.y));
      ctx.stroke();
    });

    marks.forEach((m) => {
      ctx.beginPath();
      ctx.arc(m.x, m.y, 5, 0, Math.PI * 2);
      ctx.fillStyle = "red"; // <- important
      ctx.fill();
    });
  };
  // Redraw canvas whenever lines change
  useEffect(() => {
    redrawCanvas();
  }, [lines, marks]);

  // CLICK ON BODY DIAGRAM
  const handleDiagramClick = (e) => {
    if (!activeSection) return;

    const box = imgRef.current.parentElement;
    const boxRect = box.getBoundingClientRect();
    const x = e.clientX - boxRect.left;
    const y = e.clientY - boxRect.top;

    // Adjust for image offset
    const visualX = x - canvasOffset.left;
    const visualY = y - canvasOffset.top;

    if (visualX >= 0 && visualX <= canvasOffset.width && visualY >= 0 && visualY <= canvasOffset.height) {
      setCurrentInput({
        x,
        y,
        section: activeSection,
        comment: "",
      });
    }
  }

  // MOUSE DOWN - START FREE HAND DRAWING
  const handleCanvasMouseDown = (e) => {
    if (!canvasRef.current) return;
    setIsDrawing(true);

    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setLines((prev) => [...prev, { points: [{ x, y }] }]);
  };

  // MOUSE MOVE - DRAW CONTINUOUS LINE
  const handleCanvasMouseMove = (e) => {
    if (!isDrawing || !canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setLines((prev) => {
      if (prev.length === 0) return prev;
      const newLines = [...prev];
      const lastLine = newLines[newLines.length - 1];
      newLines[newLines.length - 1] = {
        ...lastLine,
        points: [...lastLine.points, { x, y }],
      };
      return newLines;
    });
  };

  const isSectionInvalid = (sec) => {
    return errors?.physical_sections?.includes(sec);
  };

  const captureMergedImage = async () => {
    if (!imgRef.current) return null;

    const container = imgRef.current.parentElement; // The Box containing img and canvas

    try {
      const canvas = await html2canvas(container, {
        useCORS: true,
        allowTaint: false,
        backgroundColor: null, // transparent background if needed
      });
      return canvas.toDataURL("image/png");
    } catch (error) {
      console.error("Error capturing image:", error);
      return null;
    }
  };


  const dataURLtoBlob = (dataurl) => {
    const arr = dataurl.split(",");
    const mime = arr[0].match(/:(.*?);/)[1]; // get mime type
    const bstr = atob(arr[1]); // decode base64
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new Blob([u8arr], { type: mime });
  };
  // MOUSE UP - STOP DRAWING
  const handleCanvasMouseUp = () => {
    setIsDrawing(false);
  };

  // CLEAR ALL LINES
  const clearLines = () => {
    setLines([]);
    setMarks([]);
    setCapturedImage(null); // Clear captured image

    const ctx = canvasRef.current.getContext("2d");
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
  };
  // SAVE COMMENT FOR MARK
  const saveComment = () => {
    const visualX = currentInput.x - canvasOffset.left;
    const visualY = currentInput.y - canvasOffset.top;
    setMarks((prev) => [...prev, { ...currentInput, x: visualX, y: visualY }]);

    const ctx = canvasRef.current.getContext("2d");
    ctx.fillStyle = "red";
    ctx.beginPath();
    ctx.arc(visualX, visualY, 5, 0, Math.PI * 2);
    ctx.fill();

    setCurrentInput(null);
  };

  const exportCanvasImage = () => {
    if (!canvasRef.current) return null;
    return canvasRef.current.toDataURL("image/png");
  };


  const captureMergedImageBlob = () => {
    if (!imgRef.current || !canvasRef.current) return null;

    const offCanvas = document.createElement("canvas");
    offCanvas.width = imgRef.current.naturalWidth;
    offCanvas.height = imgRef.current.naturalHeight;

    const ctx = offCanvas.getContext("2d");
    ctx.drawImage(imgRef.current, 0, 0);
    ctx.drawImage(canvasRef.current, 0, 0);

    return new Promise((resolve) => {
      offCanvas.toBlob((blob) => {
        resolve(blob);
      }, "image/png");
    });
  };


  const handleNumericChange = (field, value) => {
  if (/^\d{0,3}$/.test(value)) {
    handleChange(field, value);
  }
};
  return (
    <>
      <Grid item xs={12}>
        <Card
          variant="outlined"
          sx={{
            borderRadius: 3,
            boxShadow: 3,
            backgroundColor: "#fafafa",
          }}
        >
          <CardContent>
            <Typography
              gutterBottom
              color="primary"
              sx={{
                fontWeight: "600",
                fontSize: "1rem",
                fontFamily: "Roboto, sans-serif",
              }}
            >
              ASSESSMENT BY PHYSICIAN
            </Typography>

            <Divider sx={{ mb: 2 }} />

            {/* Assessment Place + HCM Name */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Assessment Place"
                  value={formData.assessment_place}
                  onChange={(e) =>
                    handleChange("assessment_place", e.target.value)
                  }
                  error={!!errors.assessment_place}
                  helperText={errors.assessment_place}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Doctor Name"
                  value={formData.doctor_name}
                  onChange={(e) => handleChange("doctor_name", e.target.value)}
                  error={!!errors.doctor_name}
                  helperText={errors.doctor_name}
                />
              </Grid>
            </Grid>

            {/* BASELINE */}
            <Typography sx={{ mt: 3, fontWeight: "600" }}>Baseline</Typography>
            {errors.abc && (
  <Typography color="error" variant="caption">
    {errors.abc}
  </Typography>
)}
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              {/* AVPU */}
             <Grid item xs={12} sm={6} md={3}>
                <Typography fontSize={14} fontWeight={500}>
                  AVPU
                </Typography>
                <Box display="flex" flexDirection="row">
                  {["A", "V", "P", "U"].map((v) => (
                    <FormControlLabel
                      key={v}
                      control={
                        <Checkbox
                          checked={formData.baseline === baselineMap[v]}
                          onChange={() =>
                            handleChange("baseline", baselineMap[v])
                          }
                          sx={{ mr: 1 }}
                        />
                      }
                      label={v}
                      sx={{ mr: 1 }}
                    />
                  ))}
                </Box>
              </Grid>

              {/* Airway */}
             <Grid item xs={12} sm={6} md={3}>
                <Typography fontSize={14} fontWeight={500}>
                  Airway
                </Typography>
                <Box display="flex" flexDirection="row">
                  {["Open", "Closed"].map((v) => (
                    <FormControlLabel
                      key={v}
                      control={
                        <Checkbox
                          checked={formData.airway === airwayMap[v]}
                          onChange={() => handleChange("airway", airwayMap[v])}
                        />
                      }
                      label={v}
                      sx={{ mr: 1 }}
                    />
                  ))}
                </Box>
              </Grid>

              {/* Breathing */}
           <Grid item xs={12} sm={6} md={3}>
                <Typography fontSize={14} fontWeight={500}>
                  Breathing
                </Typography>
                <Box display="flex" flexDirection="row">
                  {["Present", "Absent"].map((v) => (
                    <FormControlLabel
                      key={v}
                      control={
                        <Checkbox
                          checked={formData.breathing === breathingMap[v]}
                          onChange={() =>
                            handleChange("breathing", breathingMap[v])
                          }
                        />
                      }
                      label={v}
                      sx={{ mr: 1 }}
                    />
                  ))}
                </Box>
              </Grid>

              {/* Circulation */}
            <Grid item xs={12} sm={6} md={3}>
                <Typography fontSize={14} fontWeight={500}>
                  Circulation
                </Typography>
                <Box display="flex" flexDirection="row" flexWrap="wrap">
                  {["Radial", "Present", "Absent"].map((v) => (
                    <FormControlLabel
                      key={v}
                      control={
                        <Checkbox
                          checked={formData.circulation === circulationMap[v]}
                          onChange={() =>
                            handleChange("circulation", circulationMap[v])
                          }
                        />
                      }
                      label={v}
                      sx={{ mr: 1 }}
                    />
                  ))}
                </Box>
              </Grid>

              {/* Skin Perfusion */}
              <Grid item xs={12} sm={6} md={3}>
                <Typography fontSize={14} fontWeight={500}>
                  Skin Perfusion
                </Typography>
                <Box display="flex" flexDirection="row">
                  {["Normal", "Abnormal"].map((v) => (
                    <FormControlLabel
                      key={v}
                      control={
                        <Checkbox
                          checked={
                            formData.skin_perfusion === skinPerfusionMap[v]
                          }
                          onChange={() =>
                            handleChange("skin_perfusion", skinPerfusionMap[v])
                          }
                        />
                      }
                      label={v}
                      sx={{ mr: 1 }}
                    />
                  ))}
                </Box>
              </Grid>

              {/* Numeric Vitals Stay as TextFields */}
           <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Temp (°F)"
                  value={formData.temp || ""}
                  onChange={(e) =>
                    handleNumericChange("temp", parseInt(e.target.value) || 0)
                  }
                  error={!!errors.temp}
                  helperText={errors.temp}
                />
              </Grid>

             <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Pulse (/min)"
                  value={formData.pulse || ""}
                  onChange={(e) =>
                    handleNumericChange("pulse", parseInt(e.target.value) || 0)
                  }
                  error={!!errors.pulse}
                  helperText={errors.pulse}
                />
              </Grid>
<Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="RR (/min)"
                  value={formData.rr || ""}
                  onChange={(e) =>
                    handleNumericChange("rr", parseInt(e.target.value) || 0)
                  }
                  error={!!errors.rr}
                  helperText={errors.rr}
                />
              </Grid>

          <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="BP (mmHg)"
                  value={formData.bp || ""}
                  onChange={(e) =>
                    handleNumericChange("bp", parseInt(e.target.value) || 0)
                  }
                  error={!!errors.bp}
                  helperText={errors.bp}
                />
              </Grid>

             <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="BSL (mg/dl)"
                  value={formData.bsl || ""}
                  onChange={(e) =>
                    handleNumericChange("bsl", parseInt(e.target.value) || 0)
                  }
                  error={!!errors.bsl}
                  helperText={errors.bsl}
                />
              </Grid>

         <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="SpO₂ (%)"
                  value={formData.spo2 || ""}
                  onChange={(e) =>
                    handleNumericChange("spo2", parseInt(e.target.value) || 0)
                  }
                  error={!!errors.spo2}
                  helperText={errors.spo2}
                />
              </Grid>

          <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="GCS (Out of 15)"
                  value={formData.gst_total || ""}
                  onChange={(e) =>
                    handleNumericChange("gst_total", parseInt(e.target.value) || 0)
                  }
                  error={!!errors.gst_total}
                  helperText={errors.gst_total}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
<Card
  elevation={3}
  sx={{
    p: 2,
    mt: 3,
    borderRadius: 3,
    boxShadow: 3,
    backgroundColor: "#fafafa",
  }}
>
  <Typography sx={{ fontWeight: 600, mb: 1 }}>
    Recent Investigation
  </Typography>

  <Divider sx={{ mb: 2 }} />

  <Grid container spacing={2} alignItems="flex-start">
    {/* CBC */}
    <Grid item xs={12} md={3}>
      <Typography fontSize={14} fontWeight={500}>
        CBC
      </Typography>
    </Grid>
    <Grid item xs={12} md={9}>
      <TextField
        fullWidth
        size="small"
        placeholder="CBC Remark"
        multiline
        minRows={2}
        error={Boolean(errors.recent_invesigation_cbc)}
  helperText={errors.recent_invesigation_cbc}
        value={formData.recent_invesigation_cbc || ""}
        onChange={(e) =>
          setFormData((prev) => ({
            ...prev,
            recent_invesigation_cbc: e.target.value,
          }))
        }
      />
    </Grid>

    {/* RFT */}
    <Grid item xs={12} md={3}>
      <Typography fontSize={14} fontWeight={500}>
        RFT
      </Typography>
    </Grid>
    <Grid item xs={12} md={9}>
      <TextField
        fullWidth
        size="small"
        placeholder="RFT Remark"
        multiline
        minRows={2}
        error={Boolean(errors.rft)}
  helperText={errors.rft}
        value={formData.rft || ""}
        onChange={(e) =>
          setFormData((prev) => ({
            ...prev,
            rft: e.target.value,
          }))
        }
      />
    </Grid>

    {/* BSL */}
    {/* <Grid item xs={12} md={3}>
      <Typography fontSize={14} fontWeight={500}>
        BSL
      </Typography>
    </Grid>
    <Grid item xs={12} md={9}>
      <TextField
        fullWidth
        size="small"
        placeholder="BSL Remark"
        multiline
        minRows={2}
        value={formData.bsl_remark || ""}
        onChange={(e) =>
          setFormData((prev) => ({
            ...prev,
            bsl_remark: e.target.value,
          }))
        }
      />
    </Grid> */}

    {/* Electrolytes */}
    <Grid item xs={12} md={3}>
      <Typography fontSize={14} fontWeight={500}>
        Electrolytes
      </Typography>
    </Grid>
    <Grid item xs={12} md={9}>
      <TextField
        fullWidth
        size="small"
        placeholder="Electrolytes Remark"
        multiline
        minRows={2}
            error={Boolean(errors.electrocycles)}
  helperText={errors.electrocycles}
        value={formData.electrocycles || ""}
        onChange={(e) =>
          setFormData((prev) => ({
            ...prev,
            electrocycles: e.target.value,
          }))
        }
      />
    </Grid>

    {/* Other */}
    <Grid item xs={12} md={3}>
      <Typography fontSize={14} fontWeight={500}>
        Other
      </Typography>
    </Grid>
    <Grid item xs={12} md={9}>
      <TextField
        fullWidth
        size="small"
        placeholder="Other Investigation Remark"
        multiline
        minRows={2}
             error={Boolean(errors.other)}
  helperText={errors.other}
        value={formData.other || ""}
        onChange={(e) =>
          setFormData((prev) => ({
            ...prev,
            other: e.target.value,
          }))
        }
      />
    </Grid>
  </Grid>
</Card>




        

        <Card
          elevation={3}
          sx={{
            p: 2,
            mt: 3,
            borderRadius: 3,
            boxShadow: 3,
            backgroundColor: "#fafafa",
            border: errors.physical_assessment
              ? "1px solid #d32f2f"
              : "1px solid transparent",
          }}
        >
          <Typography sx={{ fontWeight: "600", mb: 1 }}>
            Physical Assessment
          </Typography>
          {errors.physical_assessment && (
            <Typography
              color="error"
              variant="caption"
              sx={{ display: "block", mb: 1 }}
            >
              {errors.physical_assessment}
            </Typography>
          )}
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                {sections.map((sec) => (
                  <Grid item xs={12} key={sec.id}>
                  <Paper
  sx={{
    p: 1.5,
    display: "grid",
    gridTemplateColumns: {
      xs: "1fr",
      sm: "120px 60px 60px 1fr",
      md: "150px 70px 70px 1fr",
    },
    gap: 1.5,
    alignItems: "center",
    border: isSectionInvalid(sec.id)
      ? "1px solid #d32f2f"
      : "1px solid transparent",
    backgroundColor: isSectionInvalid(sec.id)
      ? "#fff5f5"
      : "transparent",
  }}
>
                      <Typography>{sec.name}</Typography>

                      <FormControlLabel
                        label="Yes"
                        control={
                          <Checkbox
                            checked={formData[sec.id] === true}
                            onChange={() => {
                              setActiveSection(sec.id);
                              setFormData((prev) => ({
                                ...prev,
                                [sec.id]: true,
                              }));
                            }}
                          />
                        }
                      />

                      {/* No Checkbox */}
                      <FormControlLabel
                        label="No"
                        control={
                          <Checkbox
                            checked={formData[sec.id] === false}
                            onChange={() => {
                              setActiveSection(null);
                              setFormData((prev) => ({
                                ...prev,
                                [sec.id]: false,
                                [`${sec.id}_remark`]: "",
                              }));
                            }}
                          />
                        }
                      />

                      {/* Remarks */}
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="Remarks / Notes"
                        value={formData[`${sec.id}_remark`] || ""}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            [`${sec.id}_remark`]: e.target.value,
                          }))
                        }
                        disabled={formData[sec.id] !== true}
                      />
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Grid>
            {/* <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                {sections.map((sec) => (
                  <Grid item xs={12} key={sec}>
                    <Paper
                      sx={{
                        p: 1,
                        display: "grid",
                        gridTemplateColumns: "150px 70px 70px 1fr", 
                        alignItems: "center",
                        gap: 2,
                        border: isSectionInvalid(sec)
                          ? "1px solid #d32f2f"
                          : "1px solid transparent",
                        backgroundColor: isSectionInvalid(sec)
                          ? "#fff5f5"
                          : "transparent",
                      }}
                    >
                      <Typography>{sec}</Typography>

                      <FormControlLabel
                        label="Yes"
                        control={
                          <Checkbox
                            checked={formData[sec] === true}
                            onChange={() => {
                              setActiveSection(sec); 
                              setFormData((prev) => ({
                                ...prev,
                                [sec]: true,
                              }));
                            }}
                          />
                        }
                      />

                      <FormControlLabel
                        label="No"
                        control={
                          <Checkbox
                            checked={formData[sec] === false}
                            onChange={() => {
                              setActiveSection(null); 
                              setFormData((prev) => ({
                                ...prev,
                                [sec]: false,
                                [`${sec}_remark`]: "", 
                              }));
                            }}
                          />
                        }
                      />

                      <TextField
                        size="small"
                        fullWidth
                        placeholder="Remarks / Notes"
                        value={formData[`${sec}_remark`] || ""}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            [`${sec}_remark`]: e.target.value,
                          }))
                        }
                        disabled={formData[sec] !== true}
                      />
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Grid> */}

            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontWeight: "600", mb: 1 }}>
                  General Body Notes
                </Typography>

                <TextField
                  multiline
                  minRows={3}
                  fullWidth
                  placeholder="Enter general comments..."
                  value={formData.General_Body_Notes || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      General_Body_Notes: e.target.value,
                    }))
                  }
                  error={!!errors.General_Body_Notes}
                  helperText={errors.General_Body_Notes}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Typography sx={{ fontWeight: "600" }}>
                    Mark Points on Body Diagram
                  </Typography>

                  <Box sx={{ mt: 1 }}>
                    <button
                      type="button"
                      onClick={async () => {
                        const mergedImage = await captureMergedImage(); // base64 image from canvas
                        if (mergedImage) {
                          const blob = dataURLtoBlob(mergedImage); // convert to proper blob
                          console.log("Captured Blob:", blob);
                          console.log("Blob URL:", URL.createObjectURL(blob));
                          setCapturedImage(mergedImage); // Display the captured image

                          // Download the image
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = 'captured_body_assessment.png';
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                          URL.revokeObjectURL(url);

                          // Save blob in formData
                          setFormData((prev) => ({
                            ...prev,
                            patient_image: blob,
                          }));

                          // You can now send it to backend
                          // Example: formData.append("file", blob, "body_assessment.png");
                        }
                      }}
                    >
                      Capture Image
                    </button>
                  </Box>

                  <Box
                    onClick={clearLines}
                    sx={{
                      px: 2,
                      py: 1,
                      backgroundColor: "#ff6b6b",
                      color: "white",
                      borderRadius: 1,
                      cursor: "pointer",
                      fontWeight: "500",
                      "&:hover": { backgroundColor: "#ff5252" },
                    }}
                  >
                    Clear Lines
                  </Box>
                </Box>
              </Box>

              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  height: 460,
                  border: "1px solid #ccc",
                  borderRadius: 2,
                  overflow: "hidden",
                  cursor: "crosshair",
                }}
              >
                {/* BODY IMAGE */}
                <img
                  ref={imgRef}
                  src={
                    formData.patient_image instanceof Blob
                      ? URL.createObjectURL(formData.patient_image)
                      : formData.patient_image || bodyDiagram
                  }
                  alt="body"
                  crossOrigin="anonymous"
                  onClick={handleDiagramClick}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    cursor: activeSection ? "crosshair" : "not-allowed",
                  }}
                />

                {/* CANVAS FOR DRAWING */}
                <canvas
                  ref={canvasRef}
                  onMouseDown={handleCanvasMouseDown}
                  onMouseMove={handleCanvasMouseMove}
                  onMouseUp={handleCanvasMouseUp}

                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    cursor: "crosshair",
                    zIndex: 3,
                  }}
                />

                {/* MARKED POINTS (Scaled) */}


                {/* POPUP COMMENT BOX */}
                {currentInput && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: currentInput.y,
                      left: currentInput.x,
                      background: "white",
                      p: 1,
                      borderRadius: 1,
                      boxShadow: 3,
                      width: 200,
                      zIndex: 10,
                    }}
                  >
                    <Typography sx={{ fontSize: 12, mb: 1 }}>
                      Comment for {currentInput.section}
                    </Typography>

                    <TextField
                      autoFocus
                      size="small"
                      fullWidth
                      placeholder="Enter note"
                      value={currentInput.comment}
                      onChange={(e) =>
                        setCurrentInput({
                          ...currentInput,
                          comment: e.target.value,
                        })
                      }
                    />
                    <Box
                      sx={{
                        mt: 1,
                        textAlign: "right",
                        cursor: "pointer",
                        fontSize: 13,
                        color: "blue",
                      }}
                      onClick={saveComment}
                    >
                      Save
                    </Box>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </Card>

        {/* Captured Image Display */}
        {/* {capturedImage && (
          <Card
            elevation={3}
            sx={{
              p: 2,
              mt: 3,
              borderRadius: 3,
              backgroundColor: "#fafafa",
            }}
          >
            <Typography sx={{ fontWeight: "600", mb: 1 }}>
              Captured Assessment Image
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ textAlign: "center" }}>
              <img
                src={capturedImage}
                alt="Captured Body Assessment"
                style={{ maxWidth: "100%", height: "auto", border: "1px solid #ccc" }}
              />
            </Box>
          </Card>
        )} */}

        <Card
          elevation={3}
          sx={{
            p: 2,
            mt: 3,
            borderRadius: 3,
            backgroundColor: "#fafafa",
          }}
        >
          <Typography sx={{ fontWeight: "600", mb: 1 }}>
            Clinical Impression of Physician
          </Typography>

          <Divider sx={{ mb: 2 }} />

          <TextField
            fullWidth
            multiline
            minRows={3}
            size="small"
            label="Write Clinical Impression"
            value={formData.clinical_impression || ""}
            onChange={(e) =>
              handleChange("clinical_impression", e.target.value)
            }
            error={!!errors.clinical_impression}
            helperText={errors.clinical_impression}
          />
        </Card>

      </Grid>
    </>
  );
}

export default StepThreeAssesment;
